import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  getFreightRmChatHistory,
  getFreightRmChatHistoryNew,
  getFreightRmChatUsers,
  getFreightRmChatUsersPaged,
  getFreightRmChatUsersUpdates,
  sendFreightRmChatMessage
} from "../../../utils/api";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://192.168.0.11:3000";

function resolveAssetUrl(path) {
  if (!path || typeof path !== "string") return null;
  const p = path.trim();
  if (!p) return null;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  return `${API_ORIGIN}${p.startsWith("/") ? "" : "/"}${p}`;
}

function mergeUserListWithUpdates(prevList, updateData) {
  const order = Array.isArray(updateData?.order) ? updateData.order.map(Number) : [];
  const rowsById = updateData?.rowsById && typeof updateData.rowsById === "object" ? updateData.rowsById : {};
  const idx = new Map((prevList || []).map((u, i) => [Number(u.id), i]));
  const next = (prevList || []).map((u) => ({ ...u }));
  for (const [k, row] of Object.entries(rowsById)) {
    const id = Number(k);
    const pos = idx.get(id);
    if (pos !== undefined) {
      next[pos] = { ...next[pos], ...row, id };
    }
  }
  for (const id of order) {
    if (!idx.has(id) && rowsById[String(id)]) {
      next.push({ ...rowsById[String(id)], id });
      idx.set(id, next.length - 1);
    }
  }
  const topIds = order.filter((id) => idx.has(id));
  if (!topIds.length) return next;
  const topSet = new Set(topIds);
  const top = topIds.map((id) => next[idx.get(id)]).filter(Boolean);
  const rest = next.filter((u) => !topSet.has(Number(u.id)));
  return [...top, ...rest];
}

function formatChatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

const UPDATES_POLL_MS = 15000;
const THREAD_POLL_MS = 4500;

const FreightRmChatsPage = () => {
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "RM chats" }
    ],
    [roleLabel]
  );

  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [hasUnreadGlobal, setHasUnreadGlobal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [thread, setThread] = useState(null);
  const [lastChatId, setLastChatId] = useState(null);

  const [messageText, setMessageText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const fullUsersRef = useRef([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadSidebar = useCallback(async () => {
    try {
      if (debouncedSearch) {
        const res = await getFreightRmChatUsersPaged({
          page: 1,
          per_page: 80,
          search: debouncedSearch
        });
        const d = res?.data ?? {};
        const list = Array.isArray(d.list) ? d.list : [];
        setUsers(list);
      } else {
        const res = await getFreightRmChatUsers();
        const d = res?.data ?? {};
        const list = Array.isArray(d.list) ? d.list : [];
        fullUsersRef.current = list;
        setUsers(list);
        setHasUnreadGlobal(!!d.hasUnread);
      }
    } catch (e) {
      showError(e.message || "Failed to load users");
      setUsers([]);
    } finally {
      setSidebarLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    setSidebarLoading(true);
    loadSidebar();
  }, [loadSidebar]);

  useEffect(() => {
    const id = window.setInterval(async () => {
      try {
        const res = await getFreightRmChatUsersUpdates({ limit: 40 });
        const d = res?.data ?? {};
        setUsers((prev) => mergeUserListWithUpdates(prev.length ? prev : fullUsersRef.current, d));
        if (d.rowsById && typeof d.rowsById === "object") {
          const anyUnread = Object.values(d.rowsById).some(
            (r) => Number(r?.unread_message_count) > 0 || String(r?.is_read) === "0"
          );
          if (anyUnread) setHasUnreadGlobal(true);
        }
      } catch {
        /* ignore poll errors */
      }
    }, UPDATES_POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  const loadThread = useCallback(async (userId) => {
    if (!userId) return;
    setThreadLoading(true);
    try {
      const res = await getFreightRmChatHistory(userId);
      const d = res?.data ?? {};
      setThread(d);
      const msgs = Array.isArray(d.messages) ? d.messages : [];
      const lid =
        d.lastChatId != null && d.lastChatId !== ""
          ? Number(d.lastChatId)
          : msgs.length
            ? Math.max(...msgs.map((m) => Number(m.id) || 0))
            : null;
      setLastChatId(Number.isFinite(lid) && lid > 0 ? lid : null);
    } catch (e) {
      showError(e.message || "Failed to load chat");
      setThread(null);
      setLastChatId(null);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedUserId) {
      setThread(null);
      setLastChatId(null);
      return;
    }
    loadThread(selectedUserId);
  }, [selectedUserId, loadThread]);

  useEffect(() => {
    if (!selectedUserId || lastChatId == null || !Number.isFinite(Number(lastChatId)) || Number(lastChatId) <= 0) {
      return undefined;
    }
    const id = window.setInterval(async () => {
      try {
        const res = await getFreightRmChatHistoryNew({
          user_id: selectedUserId,
          lastChatId
        });
        const d = res?.data ?? {};
        if (!d.newMessages) return;
        const incoming = Array.isArray(d.messages) ? d.messages : [];
        if (!incoming.length) {
          if (d.lastChatId != null) setLastChatId(Number(d.lastChatId));
          return;
        }
        setThread((prev) => {
          if (!prev) return prev;
          const existing = new Set((prev.messages || []).map((m) => Number(m.id)));
          const merged = [...(prev.messages || [])];
          for (const m of incoming) {
            if (!existing.has(Number(m.id))) merged.push(m);
          }
          merged.sort((a, b) => Number(a.id) - Number(b.id));
          const nextLast =
            d.lastChatId != null && d.lastChatId !== ""
              ? Number(d.lastChatId)
              : merged.length
                ? Math.max(...merged.map((x) => Number(x.id)))
                : lastChatId;
          setLastChatId(Number.isFinite(nextLast) ? nextLast : lastChatId);
          return { ...prev, messages: merged };
        });
      } catch {
        /* ignore */
      }
    }, THREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [selectedUserId, lastChatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [thread?.messages, threadLoading]);

  const selectedUser = useMemo(
    () => users.find((u) => Number(u.id) === Number(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      showError("Select a freight user first");
      return;
    }
    const text = messageText.trim();
    if (!text && (!files || !files.length)) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (lastChatId != null && Number(lastChatId) > 0) {
        fd.append("last_chat_id", String(lastChatId));
      } else {
        fd.append("user_id", String(selectedUserId));
      }
      fd.append("message", text);
      for (const f of files) {
        if (f) fd.append("files", f);
      }
      const res = await sendFreightRmChatMessage(fd);
      if (res?.success === false) throw new Error(res?.message || "Send failed");
      if (res?.chatId != null) setLastChatId(Number(res.chatId));
      setMessageText("");
      setFiles([]);
      await loadThread(selectedUserId);
      showSuccess("Message sent");
    } catch (err) {
      showError(err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const messages = Array.isArray(thread?.messages) ? thread.messages : [];
  const waLink = thread?.whatsappLink;
  const headerName = thread?.employeeName || selectedUser?.name || "Conversation";
  const headerCompany = thread?.companyName || selectedUser?.company_name;
  const avatarUrl = resolveAssetUrl(
    thread?.employeeAvatar || selectedUser?.profile_picture || thread?.employeePhoto
  );

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="freight-admin-shell min-w-0">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 max-w-2xl space-y-0.5">
            <h3 className="m-0 text-lg font-semibold text-gray-900">Freight RM chats</h3>
            <p className="m-0 text-xs leading-snug text-gray-600 sm:text-sm">
              Message freight users as RM; list auto-refreshes. Select a user to open the thread.
              {hasUnreadGlobal ? (
                <>
                  {" "}
                  <span className="font-medium text-amber-700">Unread in list.</span>
                </>
              ) : null}
            </p>
          </div>
          <Link to="/freight" className="btn-outline shrink-0 no-underline">
            Freight hub
          </Link>
        </div>

        {/* minmax(0,1fr) + min-h-0: sidebar stays visible; inner panes scroll */}
        <div
          className={
            "grid h-[min(78dvh,820px)] min-h-[460px] w-full max-w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm " +
            "grid-cols-1 grid-rows-[minmax(200px,34vh)_1fr] max-lg:max-h-[calc(100dvh-11rem)] " +
            "lg:grid-cols-[minmax(248px,288px)_minmax(0,1fr)] lg:grid-rows-1 lg:max-h-[min(78dvh,820px)]"
          }
        >
          <aside className="flex min-h-0 min-w-0 flex-col overflow-hidden border-b border-gray-200 bg-gray-50 lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-3 shrink-0 space-y-1.5 border-b border-gray-200 px-3 py-2.5">
              <p className="m-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Users</p>
              <input
                type="search"
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm outline-none placeholder:text-gray-400 focus:border-[#064e8d] focus:ring-1 focus:ring-[#064e8d]/25"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search freight users"
              />
            </div>
            <div className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto overscroll-contain">
              {sidebarLoading && (
                <p className="m-0 px-3 py-5 text-center text-xs text-gray-500 sm:text-sm">Loading…</p>
              )}
              {!sidebarLoading && users.length === 0 && (
                <p className="m-0 px-3 py-5 text-center text-xs text-gray-500 sm:text-sm">No matches.</p>
              )}
              {!sidebarLoading &&
                users.map((u) => {
                  const active = Number(selectedUserId) === Number(u.id);
                  const unread = Number(u.unread_message_count) > 0;
                  const av = resolveAssetUrl(u.profile_picture);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      className={
                        "flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm transition-colors hover:bg-white " +
                        (active
                          ? "border-l-[3px] border-l-[#064e8d] bg-white pl-[9px]"
                          : "border-l-[3px] border-l-transparent pl-[9px]")
                      }
                      onClick={() => setSelectedUserId(Number(u.id))}
                    >
                      <div className="relative shrink-0">
                        {av ? (
                          <img src={av} alt="" className="h-9 w-9 rounded-lg object-cover" />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-200 text-xs font-bold text-gray-600">
                            {(u.name || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        {unread ? (
                          <span
                            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border border-white bg-amber-500"
                            aria-label="Unread"
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium leading-tight text-gray-900">{u.name || `User ${u.id}`}</div>
                        <div className="truncate text-[11px] leading-tight text-gray-500">{u.company_name || "—"}</div>
                        {u.last_message ? (
                          <div className="line-clamp-2 text-[11px] leading-snug text-gray-400">{u.last_message}</div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
            </div>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-col overflow-hidden bg-white">
            {!selectedUserId && (
              <div className="flex flex-1 items-center justify-center px-4 py-10 text-center">
                <p className="m-0 max-w-sm text-xs leading-snug text-gray-600 sm:text-sm">
                  Pick a user from the list to open the chat.
                </p>
              </div>
            )}

            {selectedUserId && (
              <>
                <header className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
                    ) : (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-bold text-gray-700">
                        {headerName.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold leading-tight text-gray-900">{headerName}</div>
                      {headerCompany ? (
                        <div className="truncate text-xs leading-tight text-gray-500">{headerCompany}</div>
                      ) : null}
                    </div>
                  </div>
                  {waLink ? (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline shrink-0 px-2.5 py-1.5 text-xs sm:text-sm"
                    >
                      WhatsApp
                    </a>
                  ) : null}
                </header>

                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain bg-gray-50 px-3 py-2">
                  {threadLoading && (
                    <p className="m-0 rounded border border-gray-200 bg-white px-3 py-3 text-center text-xs text-gray-500 sm:text-sm">
                      Loading messages…
                    </p>
                  )}
                  {!threadLoading && messages.length === 0 && (
                    <p className="m-0 rounded border border-gray-200 bg-white px-3 py-3 text-center text-xs text-gray-500 sm:text-sm">
                      No messages yet — say hello below.
                    </p>
                  )}
                  {!threadLoading &&
                    messages.map((m) => {
                      const fromUser = String(m.chat_by) === "1";
                      const media = Array.isArray(m.media) ? m.media : [];
                      return (
                        <div
                          key={m.id}
                          className={
                            "max-w-[min(92%,480px)] rounded-lg border px-2.5 py-2 text-sm leading-snug " +
                            (fromUser
                              ? "self-start border-gray-200 bg-white"
                              : "self-end border-sky-200 bg-sky-50")
                          }
                        >
                          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                            {fromUser ? "User" : "RM"} · {formatChatTime(m.created_at)}
                          </div>
                          {m.message ? <div className="break-words text-gray-800">{m.message}</div> : null}
                          {media.length > 0 && (
                            <ul className="mt-2 space-y-1 border-t border-gray-200/70 pt-2 text-xs text-gray-700">
                              {media.map((item, i) => {
                                const href = item.url || resolveAssetUrl(item.file);
                                const label = item.file || `Attachment ${i + 1}`;
                                return (
                                  <li key={`${m.id}-f-${i}`} className="flex items-center gap-1.5">
                                    <span className="h-1 w-1 shrink-0 rounded-full bg-[#064e8d]/50" />
                                    {href ? (
                                      <a
                                        href={href}
                                        className="min-w-0 truncate text-[#064e8d] underline underline-offset-1"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {label}
                                      </a>
                                    ) : (
                                      <span className="min-w-0 truncate">{label}</span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  <div ref={messagesEndRef} className="h-px shrink-0" aria-hidden />
                </div>

                <form
                  className="flex shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-3 py-2.5"
                  onSubmit={handleSend}
                >
                  <textarea
                    className="min-h-[48px] w-full resize-y rounded-lg border border-gray-300 px-2.5 py-0 text-sm leading-snug outline-none placeholder:text-gray-400 focus:border-[#064e8d] focus:ring-1 focus:ring-[#064e8d]/20"
                    rows={2}
                    placeholder="Message…"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    disabled={sending}
                  />
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <label className="flex min-w-0 flex-1 flex-col gap-1 text-[11px] text-gray-500 sm:flex-row sm:items-center sm:gap-2 sm:text-xs">
                      <span className="shrink-0 whitespace-nowrap text-gray-600">Files</span>
                      <input
                        type="file"
                        multiple
                        disabled={sending}
                        className="min-w-0 flex-1 text-xs file:mr-2 file:rounded file:border file:border-gray-200 file:bg-white file:px-2 file:py-1 file:text-xs file:text-gray-700"
                        onChange={(e) => setFiles(Array.from(e.target.files || []))}
                      />
                    </label>
                    <button
                      type="submit"
                      className="btn-primary shrink-0 self-end px-2 py-0.5 sm:self-auto"
                      disabled={sending}
                    >
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>
                  {files.length > 0 ? (
                    <p className="m-0 text-[11px] text-gray-500">
                      {files.length} file{files.length === 1 ? "" : "s"} selected
                    </p>
                  ) : null}
                </form>
              </>
            )}
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightRmChatsPage;
